import {
  bodyConventionFor,
  CONVENTIONS,
  type ConventionId,
  type Position,
  type PositionBasePreset,
  type Rotation,
} from "@/shared/state/visualization/schema/v1/three-d";
import type { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

import { conventionName, type AxisPresetName } from "./axisPresetNames";
import { joinedPathFor } from "./PositionConfig/positionTopicData";

/** What the type control offers: the rotation modes plus the absence of one. */
export type RotationKind = Rotation["type"] | "none";

export const ROTATION_KINDS: readonly RotationKind[] = [
  "none",
  "euler",
  "axis_angle",
  "quaternion",
];

/** Human labels for the type control, kept beside the values they name. */
export const ROTATION_KIND_LABELS: Record<RotationKind, string> = {
  none: "None",
  euler: "Euler angles",
  axis_angle: "Axis and angle",
  quaternion: "Quaternion",
};

/** The conventions a rotation's values can be read in, in the picker's order. */
export const ROTATION_CONVENTIONS: readonly ConventionId[] = [
  "flu",
  "enu",
  "ned",
  "frd",
  "rdf",
  "scene",
];

/**
 * The convention control's rows, named as every preset picker names a
 * convention. An angle has no unit to show per axis, so there is no units line.
 */
export const ROTATION_PRESET_OPTIONS: readonly {
  id: ConventionId;
  name: AxisPresetName;
}[] = ROTATION_CONVENTIONS.map((id) => ({ id, name: conventionName(id) }));

const CONVENTION_FOR_POSITION: Record<PositionBasePreset, ConventionId> = {
  forward_left_up: "flu",
  east_north_up: "enu",
  lat_lon_alt: "enu",
  north_east_down: "ned",
  forward_right_up: "scene",
};

/**
 * The convention a new rotation on this entity starts in: its position's, so a
 * pose topic's orientation is read in the frame its position already is.
 * Forward/left/up for an entity with no position — the panel's default for
 * robot data, as it is for a point cloud.
 */
export function defaultRotationConvention(
  position: Position | undefined,
): ConventionId {
  return position?.basePreset
    ? CONVENTION_FOR_POSITION[position.basePreset]
    : "flu";
}

/**
 * What each euler channel's axis is called. A source convention names the
 * body's axes — a rotation turns the body, and a robot in an East/North/Up
 * world still has a forward/left/up one — and the scene's own axes keep their
 * letters, which is what every rotation saved before conventions was read in.
 */
function eulerAxisLabels(
  convention: ConventionId | undefined,
): Record<"x" | "y" | "z", string> {
  if (convention === undefined || convention === "scene") {
    return { x: "X", y: "Y", z: "Z" };
  }
  const { axisNames } = CONVENTIONS[bodyConventionFor(convention)];
  return {
    x: `x, ${axisNames.x}`,
    y: `y, ${axisNames.y}`,
    z: `z, ${axisNames.z}`,
  };
}

/**
 * One editable channel of a rotation: a constant or a bound topic, and a scale.
 *
 * The same shape for every mode, so the form renders a list rather than three
 * hand-written branches.
 */
export interface RotationChannel {
  /** The schema field this channel writes, e.g. `roll` or `angle`. */
  key: string;
  label: string;
  value: TopicData | number;
  /** Absent for a quaternion's components, which take no scale. */
  scale?: number;
}

/** The channels a mode edits, in the order they should be shown. */
export function channelsOf(rotation: Rotation): RotationChannel[] {
  switch (rotation.type) {
    case "euler": {
      const axis = eulerAxisLabels(rotation.convention);
      return [
        {
          key: "roll",
          label: `Roll (${axis.x})`,
          value: rotation.roll,
          scale: rotation.rollScale,
        },
        {
          key: "pitch",
          label: `Pitch (${axis.y})`,
          value: rotation.pitch,
          scale: rotation.pitchScale,
        },
        {
          key: "yaw",
          label: `Yaw (${axis.z})`,
          value: rotation.yaw,
          scale: rotation.yawScale,
        },
      ];
    }
    case "axis_angle":
      return [
        {
          key: "angle",
          label: "Angle",
          value: rotation.angle,
          scale: rotation.angleScale,
        },
      ];
    case "quaternion":
      return [
        { key: "x", label: "X", value: rotation.x },
        { key: "y", label: "Y", value: rotation.y },
        { key: "z", label: "Z", value: rotation.z },
        { key: "w", label: "W", value: rotation.w },
      ];
  }
}

/**
 * A rotation of `kind`, keeping whatever the existing one already said.
 *
 * Switching mode should not silently discard a binding the user made: an angle
 * bound to a topic stays bound when they move from axis-angle to euler, landing
 * on the first channel of the new mode. Anything with no counterpart falls back
 * to the identity rotation for that mode, which is what an entity that has never
 * been rotated should read as.
 *
 * A rotation's convention travels with it between euler and quaternion — absent
 * included, so a saved rotation is never quietly reinterpreted — and a rotation
 * made from nothing, or from an axis-angle, starts in `convention`.
 */
export function rotationOfKind(
  kind: RotationKind,
  existing: Rotation | undefined,
  convention: ConventionId,
): Rotation | undefined {
  if (kind === "none") {
    return undefined;
  }
  const carried = existing ? channelsOf(existing) : [];
  const first = carried[0]?.value;
  const firstScale = carried[0]?.scale ?? 1;
  const readIn =
    existing && existing.type !== "axis_angle"
      ? existing.convention
      : convention;
  const withReadIn = readIn ? { convention: readIn } : {};

  switch (kind) {
    case "euler":
      if (existing?.type === "euler") {
        return existing;
      }
      return {
        type: "euler",
        ...withReadIn,
        roll: first ?? 0,
        rollScale: firstScale,
        pitch: 0,
        pitchScale: 1,
        yaw: 0,
        yawScale: 1,
      };
    case "axis_angle":
      return {
        type: "axis_angle",
        // Y is the axis a joint most often turns about, and an all-zero axis is
        // no rotation at all — a worse default than a guess.
        axis: existing?.type === "axis_angle" ? existing.axis : [0, 1, 0],
        angle: first ?? 0,
        angleScale: firstScale,
      };
    case "quaternion":
      return existing?.type === "quaternion"
        ? existing
        : { type: "quaternion", ...withReadIn, x: 0, y: 0, z: 0, w: 1 };
  }
}

/**
 * `rotation` read in `convention` instead. An axis-angle is left alone: its
 * axis is the model's own, from the file, and has no other frame to be read in.
 */
export function withConvention(
  rotation: Rotation,
  convention: ConventionId,
): Rotation {
  return rotation.type === "axis_angle"
    ? rotation
    : { ...rotation, convention };
}

/**
 * `rotation` with one channel replaced.
 *
 * Written generically so the form can drive every mode through one handler; the
 * cast is contained here rather than at each of the eight call sites a
 * per-channel handler would need.
 */
export function withChannel(
  rotation: Rotation,
  key: string,
  value: TopicData | number,
): Rotation {
  return { ...rotation, [key]: value } as Rotation;
}

/** `rotation` with one channel's scale replaced. */
export function withChannelScale(
  rotation: Rotation,
  key: string,
  scale: number,
): Rotation {
  return { ...rotation, [`${key}Scale`]: scale } as Rotation;
}

/** `rotation` with one component of an axis-angle's axis replaced. */
export function withAxisComponent(
  rotation: Rotation,
  index: 0 | 1 | 2,
  value: number,
): Rotation {
  if (rotation.type !== "axis_angle") {
    return rotation;
  }
  const axis: [number, number, number] = [
    rotation.axis[0],
    rotation.axis[1],
    rotation.axis[2],
  ];
  axis[index] = value;
  return { ...rotation, axis };
}

/**
 * The constant a channel's typed text stands for — a finite number, in the
 * channel's units before its scale — or undefined when the text is not one.
 *
 * The same reading a joint binding gives free text: a field that takes paths
 * can take a number instead, and an axis held at a constant is how a fixed
 * offset in the data is stated — a sensor mounted a quarter turn off its
 * robot's forward, say — without a topic to bind. Models need no such
 * constant: the loader already turns every converted asset to face +Z.
 */
export function rotationConstantFor(text: string): number | undefined {
  const trimmed = text.trim();
  if (trimmed === "") {
    return undefined;
  }
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * What a channel reads as in its path field: the path it is bound to, the
 * constant it holds, or nothing for zero — the identity every channel starts
 * at, which an empty field states better than a `0` that looks like a path.
 */
export function channelText(value: TopicData | number): string | undefined {
  if (typeof value === "number") {
    return value === 0 ? undefined : String(value);
  }
  return joinedPathFor(value);
}
